import {
  Component,
  OnInit,
  ChangeDetectorRef,
  ViewChild,
  NgZone,
  AfterViewInit,
  TemplateRef,
} from '@angular/core';
import { Router } from '@angular/router';
import { Paginator } from 'primeng/paginator';
import { MessageService } from 'primeng/api';
import {
  debounceTime,
  distinctUntilChanged,
  Subject,
  Subscription,
} from 'rxjs';
import { StorageService } from 'src/app/services/storage.service';
import { OrderService } from 'src/app/services/order.service';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { RazorpayService } from 'src/app/services/razorpay.service';
import { TableColumn } from 'src/app/shared-components/data-table/data-table.component';

interface Transaction {
  slNo: number;
  clientVendor: string;
  transactionId: string;
  orderId: string;
  dateTime: string;
  purpose: string;
  status: 'Success' | 'In-progress' | 'Failed';
  transactionType: 'Debit' | 'Credit';
  totalAmount: number;
}

@Component({
    selector: 'app-razorpay-transaction',
    templateUrl: './razorpay-transaction.component.html',
    styleUrls: ['./razorpay-transaction.component.scss'],
    standalone: false
})
export class RazorpayTransactionComponent implements OnInit {
  readonly columns: TableColumn[] = [
    { field: 's_no', header: 'Sl. No' },
    { field: 'order_id', header: 'Order ID' },
    {
      field: 'payment_id',
      header: 'Payment ID',
      accessorFn: (row) => row.razorpay_link_id || row.hdfc_order_id || '--',
    },
    { field: 'created_on', header: 'Date & Time' },
    { field: 'payee_name', header: 'Payee' },
    { field: 'payee_contact', header: 'Contact' },
    { field: 'status', header: 'Status' },
    { field: 'amount', header: 'Total Amount' },
  ];

 @ViewChild('viewOrderData') viewOrderData: any;
  @ViewChild('paymentSummaryModal') paymentSummaryModal!: TemplateRef<any>;
  // Tab management
  activeTab = 'transactions';

  // Transactions data
  transactions: Transaction[] = [];
  filteredTransactions: Transaction[] = [];

  // Common pagination
  currentPage = 1;
  itemsPerPage = 10;
  totalPages: number;

  // Transaction filters
  searchTerm = '';
  dateRange: Date[] = [];
  statusFilter: any = { label: 'Status: All', value: 'All' };
  typeFilter: any = { label: 'Type: All', value: 'All' };

  // Dropdown options
  statusOptions = [
    { label: 'Status: All', value: 'All' },
    { label: 'Success', value: 'PAYMENT_SUCCESSFUL' },
    { label: 'Pending', value: 'PAYMENT_PENDING' },
    // { label: 'Failed', value: 'PAYMENT_FAILED' },
  ];

  typeOptions = [
    { label: 'Type: All', value: 'All' },
    { label: 'Debit', value: 'Debit' },
    { label: 'Credit', value: 'Credit' },
  ];

  numberoftables: number;
  searchPageNumber: number = 1;
  totalRecords: any;
  pageNo: number = 1;
  rowsPerPage: number = 10;
  isTransactionsLoading: boolean;
  filteredData: any[] = [];
  customerName: string;
  currentBalance: string;
  virtualAccountNo: string;
  bankName: string;
  isWallet: boolean = false;
  viewOrderId: any;
  paymentAmountData: any;
  accountId: any;
  private searchSubject = new Subject<string>();
  private subscription = new Subscription();
  private readonly MAX_SEARCH_LENGTH = 50;
  userRole: string;

  constructor(
    private router: Router,
    private razorpayService: RazorpayService,
    private storageService: StorageService,
    private messageService: MessageService,
    private orderService: OrderService,
    private modalService: NgbModal,
    private cdr: ChangeDetectorRef
  ) {
    this.virtualAccountNo = this.storageService.getVirtualAccountNumber();
    this.isWallet = this.storageService.getIsWallet();
    this.accountId = this.storageService.getAccountId();
    this.userRole = this.storageService.getRole() || null;
  }

  ngOnInit(): void {
    this.subscription = this.searchSubject
      .pipe(debounceTime(1000), distinctUntilChanged())
      .subscribe(() => {
        this.searchTerm = this.searchTerm.trim();
        this.currentPage = 1;
        this.loadTransactionsData();
      });
    this.loadTransactionsData();
  }


  // =================== TRANSACTIONS SECTION ===================

  loadTransactionsData() {
    this.isTransactionsLoading = true;

    const params = {
      page_number: this.currentPage,
      page_size: this.itemsPerPage,
      order_id: this.searchTerm.trim(),
      payment_status: this.statusFilter?.value !== 'All' ? this.statusFilter.value : '',
      transaction_type:
        this.typeFilter?.value !== 'All' ? this.typeFilter.value : '',
      from_date:
        this.dateRange && this.dateRange[0]
          ? this.formatDateForAPI(this.dateRange[0])
          : '',
      to_date:
        this.dateRange && this.dateRange[1]
          ? this.formatDateForAPI(this.dateRange[1])
          : '',
    };

    // Remove empty parameters
    Object.keys(params).forEach((key) => {
      if (
        params[key] === '' ||
        params[key] === null ||
        params[key] === undefined
      ) {
        delete params[key];
      }
    });

    this.razorpayService
      .getTransactionDetails(this.accountId, params)
      .subscribe({
        next: (response) => {
          const rawData = response.content?.data || response.content;
          this.transactions = Array.isArray(rawData) ? rawData : [];
          this.totalRecords =
            response.pagination?.total_records ||
            response.totalElements ||
            response.total_record ||
            this.transactions.length;
          this.numberoftables = Math.ceil(
            this.totalRecords / this.itemsPerPage
          );
          this.searchPageNumber = this.currentPage;
          this.isTransactionsLoading = false;
          this.cdr.detectChanges();
        },
        error: (error) => {
          console.error('Error loading transactions:', error);
          this.transactions = [];
          this.totalRecords = 0;
          this.numberoftables = 0;
          this.isTransactionsLoading = false;
          this.cdr.detectChanges();
        },
      });
  }

  private formatDateForAPI(date: Date): string {
    // Use local date components instead of UTC conversion
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');

    return `${year}-${month}-${day}`; // Returns YYYY-MM-DD format in local timezone
  }

  // =================== FILTER HANDLERS ===================
  onSearch() {
    this.searchTerm = this.sanitizeSearchInput(this.searchTerm);
    this.searchSubject.next(this.searchTerm);
  }
  
  private sanitizeSearchInput(input: string): string {
  if (!input) return '';
  let sanitized = input.trim().substring(0, this.MAX_SEARCH_LENGTH);
  sanitized = sanitized.replace(/[^a-zA-Z0-9_-]/g, '');
  return sanitized;
 }

  onDateRangeChange() {
    this.currentPage = 1;
    this.loadTransactionsData();
  }

  onStatusChange() {
    this.currentPage = 1;
    this.loadTransactionsData();
  }

  onTypeChange() {
    this.currentPage = 1;
    this.loadTransactionsData();
  }

  // =================== PAGINATION HANDLERS ===================
  onPageChange(event: any) {
    this.currentPage = event.page + 1;
    this.itemsPerPage = event.rows;
    this.loadTransactionsData();
  }

  pageChange(page: number) {
    if (page >= 1 && page <= this.numberoftables) {
      this.currentPage = page;
      this.loadTransactionsData();
    }
  }

  // =================== TRACKBY FUNCTIONS ===================
  trackByTransactionId(index: number, transaction: Transaction): any {
    return transaction.transactionId || index;
  }

  trackByCustomerId(index: number, customer: any): any {
    return customer.id || customer.account_number || index;
  }

  // =================== NAVIGATION METHODS ===================
  createWallet() {
    this.router.navigate(['doqfy/wallet/create-wallet']);
  }

  moneyTransfer() {
    this.router.navigate(['doqfy/wallet/money-transfer']);
  }

  exportData() {
    // Implementation for export data
    const exportParams = {
      // search: this.searchTerm.trim(),
      // status: this.statusFilter?.value !== 'All' ? this.statusFilter.value : '',
      // type: this.typeFilter?.value !== 'All' ? this.typeFilter.value : '',
      dateFrom:
        this.dateRange && this.dateRange[0]
          ? this.formatDateForAPI(this.dateRange[0])
          : '',
      dateTo:
        this.dateRange && this.dateRange[1]
          ? this.formatDateForAPI(this.dateRange[1])
          : '',
    };

    // Call your export API with current filters
    let fileName = 'wallet_report';
    // this.walletService
    //   .exportTransactions(this.virtualAccountNo, exportParams)
    //   .subscribe((res) => {
    //     if (res.status === 200 && res['message'] != 'No records found') {
    //       this.walletService.base64ToCSV(res['content'], fileName);
    //       this.messageService.add({
    //         severity: 'success',
    //         summary: 'File downloaded successfully !',
    //         icon: 'pi-check-circle',
    //       });
    //     } else {
    //       this.messageService.add({ severity: 'warn', summary: res.message });
    //     }
    //   });
  }

  goBack() {
    this.router.navigate(['/doqfy/admin-dashboard']);
  }

  navigateToOrder(transaction: any) {
    const orderId = transaction.order_id;
    this.viewOrder(orderId, this.viewOrderData);

  }

  getAmountSummary(transaction: any) {
    const orderId = transaction.order_id;
    const is_pre_order = transaction.additional_data.description === 'Pre-Procurement Payment'

    const modalRef = this.modalService.open(this.paymentSummaryModal, {
      centered: true,
      size: 'md'
    });

    this.orderService.getPaymentAmountDetails(orderId, is_pre_order).subscribe(
      (res) => {
        this.paymentAmountData = res['content'];
      },
      (error) => {
        console.error('Error fetching payment details:', error);
        modalRef.close();
      }
    );
  }

  viewOrder(order_id, content) {
    this.viewOrderId = { orderId: order_id, bulkOrderid: null };
    this.modalService
      .open(content, {
        size: 'xl',
        ariaLabelledBy: 'modal-basic-title',
        backdrop: 'static',
        keyboard: false,
      })
      .result.then(
        (result) => {
          console.log(result);
        },
        (reason) => {
          console.log(reason);
          // this.getOrderDetail(order_id, content);
        }
      );
  }

}